import { POINT, MAIL_RECORD_MESSAGES_PATH, MAIL_RECORD_MESSAGES_LIMIT, fill, formatDateTime, t, type DateFormat, type RecordMessageRow } from '@bms2/shared';
import { useDateFormat } from '../../shell/ViewerZone';
import { useApi } from '../records/api';
import documentStyles from '../records/DocumentPage.module.css';
import styles from './mail.module.css';


/** 🔒 An open is evidence, not reading (decision 0162): *Link opened* with the count and the last time, never *read*. */
export function openedText(row: Pick<RecordMessageRow, 'opens' | 'lastOpenedAt'>, zone: string, dateFormat: DateFormat, withLabel = true): string | null {
  if (row.opens === 0 || !row.lastOpenedAt) return null;
  const facts = [`${row.opens} ${row.opens === 1 ? t('mail.opened.time') : t('mail.opened.times')}`, `${t('mail.opened.last')} ${formatDateTime(row.lastOpenedAt, zone, dateFormat)}`];
  return (withLabel ? [t('mail.opened.label'), ...facts] : facts).join(` ${POINT} `);
}

/**
 * The mail sent about a record, on the record's own page (piece 32; decisions
 * 0162, 0791): to whom, from which address, by whom and when, whether it went,
 * and the link's opens. 🔒 T2371: the received mail tagged to the record lists
 * beside it, newest first: a send by when it went, a received message by when
 * it arrived (T3317). The message itself stays in its mailbox; the panel lists
 * the newest fifty of both together and says so when it is full.
 */
export function RecordMessagesPanel({ recordType, recordId, timeZone, apiBaseUrl = '' }: { recordType: string; recordId: string; timeZone: string; apiBaseUrl?: string }) {
  const read = useApi<RecordMessageRow[]>(`${MAIL_RECORD_MESSAGES_PATH}?${new URLSearchParams({ recordType, recordId }).toString()}`, apiBaseUrl);
  const dateFormat = useDateFormat();
  const answered = read.state.kind === 'ready' && Array.isArray(read.state.data) ? read.state.data : [];
  // 🔒 T3339: the answer carries one row over the limit when older mail is left out, so the note names a cut only where there is one.
  const cut = answered.length > MAIL_RECORD_MESSAGES_LIMIT;
  const rows = cut ? answered.slice(0, MAIL_RECORD_MESSAGES_LIMIT) : answered;
  if (rows.length === 0) return null;
  return (
    <section className={documentStyles.block} data-record-messages={rows.length}>
      <h2 className={documentStyles.heading}>{t('mail.recordMessages.title')}</h2>
      <ul className={styles.list}>
        {rows.map((row) => {
          // 🔒 T2371: a received message tagged here reads by its sender and its recipients, beside the sends.
          if (row.direction === 'Inbound') {
            const when = row.receivedAt ?? row.sentAt ?? null;
            return (
              <li key={row.id} className={styles.column} data-record-message={row.id} data-record-message-direction="Inbound">
                <span>{[when ? formatDateTime(when, timeZone, dateFormat) : null, row.fromAddress].filter(Boolean).join(` ${POINT} `)}</span>
                <span className={styles.meta}>{[t('mail.recordMessages.received'), row.to.length > 0 ? `${t('mail.recordMessages.to')} ${row.to.join(', ')}` : null].filter(Boolean).join(` ${POINT} `)}</span>
              </li>
            );
          }
          const opened = openedText(row, timeZone, dateFormat);
          const status = row.deliveryStatus === 'Deferred' ? t('mail.recordMessages.notSent') : row.deliveryStatus === 'Bounced' ? t('mail.recordMessages.bounced') : null;
          return (
            <li key={row.id} className={styles.column} data-record-message={row.id} data-record-message-direction="Outbound">
              <span>{[row.sentAt ? formatDateTime(row.sentAt, timeZone, dateFormat) : null, row.to.join(', ')].filter(Boolean).join(` ${POINT} `)}</span>
              <span className={styles.meta}>{[`${t('mail.recordMessages.from')} ${row.fromAddress}`, row.sentBy ? `${t('mail.pane.sentBy')} ${row.sentBy}` : null, status].filter(Boolean).join(` ${POINT} `)}</span>
              {opened ? <span className={styles.meta} data-record-message-opened={row.opens}>{opened}</span> : null}
            </li>
          );
        })}
      </ul>
      {cut ? <p className={styles.meta} data-record-messages-cap={MAIL_RECORD_MESSAGES_LIMIT}>{fill(t('mail.recordMessages.capNote'), { count: MAIL_RECORD_MESSAGES_LIMIT })}</p> : null}
    </section>
  );
}
